import { defineTheme, DEFAULT_ICONS, type IconName, type ThemeIcon } from '../contract';
import { SOUND_EVENTS, type SoundPack } from '../../audio/sound-pack';
import { packAssets } from '../assets';
import './styles.css';
import license from './fonts/OFL-Fraunces.txt?raw';

const asset = packAssets(
  import.meta.glob('./{images,icons,audio}/*', { eager: true, query: '?url', import: 'default' }),
);
const texture = (name: string) => `url("${asset(`images/${name}.svg`)}")`;

export default defineTheme({
  id: 'moonpetal',
  name: 'Moonpetal Atelier',
  colorScheme: 'light',
  description:
    'A little spellbook beneath a lavender moon. Inked sigils, floating fireflies, and a music-box garden.',
  credits:
    'Original Spellie illustration, icons and synthesized music. Fraunces by Undercase Type, SIL OFL 1.1.',
  license,
  preview: asset('images/garden.svg'),
  tokens: {
    background: '#eee3ef',
    foreground: '#362d43',
    surface: '#fff8ed',
    surfaceText: '#362d43',
    mutedText: '#6c536e',
    border: '#aa839c',
    accent: '#74457e',
    primary: 'linear-gradient(145deg, #795486, #573962)',
    primaryText: '#fff7ea',
    selected: '#70467c',
    selectedText: '#fff8ed',
    tile: 'linear-gradient(155deg, #fffdf1, #f0dec7)',
    tileText: '#493349',
    tileSelected: 'linear-gradient(145deg, #86629a, #644577)',
    tileSelectedText: '#fffaf0',
    tileHead: 'linear-gradient(145deg, #fff5c7, #ecd095)',
    tileHeadText: '#493349',
    success: '#32614d',
    connectionConnected: '#32614d',
    podiumGold: '#b0873a',
    podiumSilver: '#8d87a4',
    podiumBronze: '#a4674e',
    winnerBackground: 'linear-gradient(135deg, #fbeccd, #fff8ed 70%)',
    winnerShadow: '0 4px 18px #b0873a33',
    connectionReconnecting: '#a47b00',
    connectionReconnectingAnimation: 'moonpetal-connection-pulse 1.6s ease-in-out infinite',
    danger: '#943e54',
    highlight: '#f1e3f1',
    path: '#ad70b6',
    overlay: '#34243e59',
    focus: '#734286',
    font: '"Moonpetal Fraunces", Georgia, serif',
    boardFont: '"Moonpetal Fraunces", Georgia, serif',
    displayFont: '"Moonpetal Fraunces", Georgia, serif',
    fontWeight: '450',
    headingWeight: '650',
    letterSpacing: '0.01em',
    surfaceShadow: '0 8px 30px #56335b1c, inset 0 0 0 4px #fff9ee, inset 0 0 0 5px #d8b9bc',
    controlShadow: '0 3px 0 #bda0b8, 0 5px 12px #513a5917',
    tileShadow: '0 4px 0 #bb9f8b, inset 0 0 0 3px #fff9ed',
    cardRadius: '28px 8px 28px 8px',
    buttonRadius: '20px 8px 20px 8px',
    tileRadius: '26% 12% 26% 12%',
    inputRadius: '14px 5px 14px 5px',
    chipRadius: '12px 4px',
    dialogRadius: '30px 10px',
    sheetRadius: '30px 10px 0 0',
    cardBorder: '1px solid #a585a4',
    buttonBorder: '1px solid #a988aa',
    tileBorder: '1px solid #b79792',
    inputBorder: '1px solid #a988aa',
    buttonBackground: 'linear-gradient(135deg, #fff8ec, #f3e5ef)',
    inputBackground: '#fffaf1',
    cardTextureSize: '100px 100px',
    buttonTextureSize: '80px 80px',
    hudBackground: '#fff8ed',
    feedbackBackground: '#fff8ed',
    dialogShadow: '0 8px 30px #56335b1c',
    dialogBackground: '#f3e5f1',
    dialogText: '#362d43',
    dialogTexture: texture('modal-parchment'),
    cardTexture: texture('paper'),
    tileTexture: texture('sigil'),
    buttonTexture: texture('paper'),
    buttonHover: '#eddbef',
    buttonPress: 'translateY(2px) scale(0.98)',
    buttonHoverTransform: 'translateY(-2px)',
    tileSelectTransform: 'scale(0.93) rotate(-3deg)',
    tileSelectedShadow: '0 0 16px #be86d570, inset 0 0 0 2px #ddc0e7',
    tileHeadOutline: '2px solid #926d43',
    tileHeadShadow: '0 0 18px #f8d580aa',
    pathWidth: '3.5',
    pathDash: '1 3',
    pathGlow: 'drop-shadow(0 0 2px #ba76c9)',
    pathAnimation: 'moonpetal-thread 1.2s linear infinite',
    interactionDuration: '160ms',
    interactionEasing: 'cubic-bezier(.2,.8,.3,1)',
    spinnerTrack: 'color-mix(in srgb, var(--theme-accent) 25%, transparent)',
    spinnerColor: 'var(--theme-accent)',
    spinnerRadius: '40% 60% 40% 60%',
    spinnerAnimation: 'moonpetal-spinner 1.6s ease-in-out infinite',
    enterAnimation: 'moonpetal-arrive 420ms ease-out both',
    revealAnimation: 'moonpetal-unfold 500ms ease-out both',
    selectAnimation: 'moonpetal-ink 200ms ease-out',
    countdownAnimation: 'moonpetal-count 900ms ease-out both',
    urgentAnimation: 'moonpetal-urgent 900ms ease-in-out infinite alternate',
    acceptAnimation: 'moonpetal-accept 280ms ease-out both',
    powerUpFeedbackBorder: '3px double currentColor',
    powerUpFeedbackShadow: '0 0 18px color-mix(in srgb, currentColor 45%, transparent)',
    powerUpCollectAnimation: 'moonpetal-power-up-collect 800ms ease-out',
    powerUpHitAnimation: 'moonpetal-power-up-hit 700ms ease-out',
    powerUpBlockedAnimation: 'moonpetal-power-up-blocked 800ms ease-out',
    rejectAnimation: 'moonpetal-reject 350ms ease-in-out both',
    duplicateAnimation: 'moonpetal-duplicate 300ms ease-out both',
    resultsAnimation: 'moonpetal-arrive 700ms ease-out both',
    dialogAnimation: 'moonpetal-arrive 400ms ease-out both',
    welcomeAnimation: 'moonpetal-open 300ms ease-out',
    decorationAnimation: 'moonpetal-float 7s ease-in-out infinite alternate',
    titleShadow: '0 2px 0 #fff8ed, 0 5px 22px #aa83bd50',
  },
  icons: Object.fromEntries(
    Object.keys(DEFAULT_ICONS).map((name) => [
      name,
      { src: asset(name === 'logo' ? 'images/emblem.svg' : `icons/${name}.svg`) },
    ]),
  ) as Record<IconName, ThemeIcon>,
  background: [
    {
      src: asset('images/garden.svg'),
      mobileSrc: asset('images/garden-portrait.svg'),
      depth: 5,
      position: 'center bottom',
    },
    {
      src: asset('images/fireflies.svg'),
      depth: 14,
      opacity: 0.8,
      animation: 'moonpetal-float 9s ease-in-out infinite alternate',
    },
  ],
  sounds: Object.fromEntries(
    [...SOUND_EVENTS, 'music', 'ambience'].map((event) => [
      event,
      {
        src: asset(`audio/${event}.wav`),
        volume: event === 'music' ? 0.55 : event === 'ambience' ? 0.4 : 0.8,
      },
    ]),
  ) as SoundPack,
  // Enchanted tiles: gilt, twin moons, a scrying eye, a wax seal, frost, an ink tax, a twirl and mist.
  powerUps: {
    gold: {
      glyph: '"✦"',
      glyphColor: '#7a5a12',
      background: 'linear-gradient(150deg, #fff3c2, #f2cf6a)',
      text: '#5a3f0c',
      border: '2px solid #c9a84a',
      shadow: '0 0 18px #f5d67a99',
      animation: 'moonpetal-gilt 1.6s ease-in-out infinite alternate',
    },
    double: {
      glyph: '"☾☾"',
      glyphColor: '#fff6d5',
      background: 'linear-gradient(150deg, #86629a, #5a3f70)',
      text: '#fff6e8',
      border: '2px solid #cdb3dc',
      shadow: '0 0 16px #a887c288',
      animation: 'moonpetal-twin 2s ease-in-out infinite alternate',
    },
    reveal: {
      glyph: '"◎"',
      glyphColor: '#2f5d6b',
      background: 'radial-gradient(circle at 50% 45%, #dff6f7, #a9d4dd)',
      text: '#22454f',
      border: '2px solid #6fa7b3',
      animation: 'moonpetal-scry 2.4s ease-in-out infinite',
    },
    shield: {
      glyph: '"❦"',
      glyphColor: '#7a2438',
      background: 'linear-gradient(150deg, #f8e2d6, #e6b8a5)',
      text: '#5a2230',
      border: '2px solid #b6606f',
      shadow: 'inset 0 0 0 4px #f8e2d6, inset 0 0 0 6px #b6606f',
      animation: 'moonpetal-seal 1.8s ease-in-out infinite alternate',
    },
    freeze: {
      glyph: '"❅"',
      glyphColor: '#3d6f9c',
      background: 'linear-gradient(160deg, #f2fbff, #c6e3f5)',
      text: '#2b4e70',
      border: '2px solid #8fbfe0',
      shadow: '0 0 14px #b8def5aa',
      animation: 'moonpetal-frost 2.2s ease-in-out infinite alternate',
    },
    tax: {
      glyph: '"☍"',
      glyphColor: '#f2d7c7',
      background: 'linear-gradient(150deg, #5a4a52, #3b2d38)',
      text: '#f2e2d8',
      border: '2px solid #8b6f7c',
      animation: 'moonpetal-ink-drip 1.4s ease-in infinite',
    },
    spin: {
      glyph: '"❁"',
      glyphColor: '#7a3a6a',
      background: 'linear-gradient(150deg, #f9d6ea, #e2a4c9)',
      text: '#5c2a4e',
      border: '2px solid #c57aa6',
      animation: 'moonpetal-twirl 3s linear infinite',
    },
    fog: {
      glyph: '"〰"',
      glyphColor: '#5d6d75',
      background: 'linear-gradient(150deg, #e6ebee, #c3ccd2)',
      text: '#3b474d',
      border: '2px solid #9aa9b1',
      animation: 'moonpetal-mist 2.6s ease-in-out infinite alternate',
    },
  },
  haptics: {
    select: [6],
    backtrack: [4],
    accept: [10, 35, 12],
    reject: [22],
    duplicate: [8],
    countdown: [8],
    start: [12, 35, 20],
    end: [25],
    win: [12, 40, 12, 40, 28],
    results: [12],
    powerUpGold: [14, 30, 14, 30, 22],
    powerUpDouble: [12, 40, 12],
    powerUpReveal: [8, 20, 8],
    powerUpShield: [18, 30, 18],
    powerUpFreeze: [30],
    powerUpTax: [10, 20, 10, 20, 10],
    powerUpSpin: [6, 15, 6, 15, 6, 15, 6],
    powerUpFog: [20, 40, 20],
    powerUpHit: [26, 30, 16],
    powerUpBlocked: [12, 12, 12],
  },
});
